
import React, { useState } from "react";
import ImageUploader from "@/components/ImageUploader";
import ImageGrid from "@/components/ImageGrid";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FolderPlus, Upload } from "lucide-react";
import { Image, Album } from "@/lib/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

interface ImagesTabProps {
  images: Image[];
  albums: Album[];
  onDeleteImage: (id: string) => Promise<void>;
  onImageUpload: (newImage: Image) => void;
  loading: boolean;
  selectedImages: string[];
  onToggleSelect: (imageId: string) => void;
  setSelectedAlbum: (album: Album | null) => void;
  selectedAlbum: Album | null;
  onAddImagesToAlbum: () => Promise<void>;
  setAddToAlbumOpen: (open: boolean) => void;
  addToAlbumOpen: boolean;
}

const ImagesTab = ({
  images,
  albums,
  onDeleteImage,
  onImageUpload,
  loading,
  selectedImages,
  onToggleSelect,
  setSelectedAlbum,
  selectedAlbum,
  onAddImagesToAlbum,
  setAddToAlbumOpen,
  addToAlbumOpen,
}: ImagesTabProps) => {
  const [uploadToAlbum, setUploadToAlbum] = useState<string | null>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const handleImageUpload = async (newImage: Image) => {
    // Call the parent's upload handler first
    onImageUpload(newImage);

    // If an album is selected, add the image to that album
    if (uploadToAlbum) {
      const album = albums.find(a => a.id === uploadToAlbum);
      if (album) {
        setSelectedAlbum(album);
        await addImageToAlbum(newImage.id, uploadToAlbum);
      }
    }
  };

  const addImageToAlbum = async (imageId: string, albumId: string) => {
    try {
      const { error } = await supabase
        .from('album_images')
        .insert({ album_id: albumId, image_id: imageId });
      
      if (error) throw error;
    } catch (error) {
      console.error("Error adding image to album:", error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">My Images</h2>
        <div className="flex gap-2">
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Upload className="h-4 w-4 mr-2" />
                Upload to Album
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <h2 className="text-lg font-semibold mb-4">
                Upload to Album
              </h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="album-select">Select an album</Label>
                  <Select 
                    value={uploadToAlbum || ""} 
                    onValueChange={(value) => setUploadToAlbum(value)}
                  >
                    <SelectTrigger id="album-select">
                      <SelectValue placeholder="Select an album" />
                    </SelectTrigger>
                    <SelectContent>
                      {albums.length === 0 ? (
                        <SelectItem value="disabled" disabled>No albums available</SelectItem>
                      ) : (
                        albums.map((album) => (
                          <SelectItem key={album.id} value={album.id}>
                            {album.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <ImageUploader 
                  onUpload={handleImageUpload} 
                  onSuccess={() => setIsUploadDialogOpen(false)}
                />
              </div>
            </DialogContent>
          </Dialog>
          
          {selectedImages.length > 0 && (
            <Dialog open={addToAlbumOpen} onOpenChange={setAddToAlbumOpen}>
              <DialogTrigger asChild>
                <Button>
                  <FolderPlus className="h-4 w-4 mr-2" />
                  Add {selectedImages.length} to Album
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <h2 className="text-lg font-semibold mb-4">
                  Add to Album
                </h2>
                {albums.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-white/60 mb-4">You don't have any albums yet. Create one first.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {albums.map(album => (
                        <Button
                          key={album.id}
                          variant={selectedAlbum?.id === album.id ? "default" : "outline"}
                          className="justify-start overflow-hidden"
                          onClick={() => setSelectedAlbum(album)}
                        >
                          <span className="truncate">{album.name}</span>
                        </Button>
                      ))}
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <Button variant="ghost" onClick={() => setAddToAlbumOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={onAddImagesToAlbum} disabled={!selectedAlbum}>
                        Add to Album
                      </Button>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      <ImageUploader onUpload={onImageUpload} />
      <ImageGrid 
        images={images}
        onDelete={onDeleteImage}
        loading={loading}
        selectable={true}
        selectedImages={selectedImages}
        onToggleSelect={onToggleSelect}
      />
    </div>
  );
};

export default ImagesTab;
